import {
    Mesh,
    MeshStandardMaterial,
    type BufferGeometry,
    type Group,
    type Material,
    type Object3D,
} from 'three'
import type OwnedGLBResources from './OwnedGLBResources'
import { copyVariantMaterial } from './copyVariantMaterial'
import type {
    GLBMaterialVariants,
    MaterialVariantObject,
    SourceMaterialVariantFunctions,
    SourceMaterialVariantTable,
} from './types'

type MaterialSelection = Material | Material[]
type MaterialMesh = Mesh<BufferGeometry, MaterialSelection>

const getMaterialVariantTable = (
    object:Object3D,
):SourceMaterialVariantTable | undefined => (
    object.userData.variantMaterials as (
        SourceMaterialVariantTable | undefined
    )
)

const normalizeMaterialVariantMaterials = (
    scene:Group,
    resources:OwnedGLBResources,
) => {
    scene.traverse(object => {
        const table = getMaterialVariantTable(object)
        if(!table) return

        Object.values(table).forEach(entry => {
            if(entry.material) {
                entry.material = resources.ownMaterial(entry.material)
            }
        })

        const variantObject = object as MaterialVariantObject
        if(variantObject.userData.originalMaterial) {
            variantObject.userData.originalMaterial = resources.ownMaterial(
                variantObject.userData.originalMaterial,
            )
        }
        if(object instanceof Mesh) {
            const mesh = object as MaterialMesh
            mesh.material = resources.ownMaterialSelection(
                mesh.material,
            )
        }
    })
}

/**
 * Build the viewer-owned facade around the GLTF KHR material-variant plugin.
 *
 * The controller serializes lazy variant loading, ignores stale selections,
 * and maintains stable mesh material identities for accumulated shadows.
 */
export const createMaterialVariantController = (
    scene:Group,
    sourceFunctions:Partial<SourceMaterialVariantFunctions> | undefined,
    resources:OwnedGLBResources,
):GLBMaterialVariants | undefined => {
    const ensureSourceVariants = sourceFunctions?.ensureLoadVariants
    const selectSourceVariant = sourceFunctions?.selectVariant
    if(!ensureSourceVariants || !selectSourceVariant) return undefined

    let loading:Promise<void> | null = null
    let selectionRevision = 0
    const displayMaterials = new WeakMap<Object3D, Material>()

    // Drei's accumulated-shadow pass remembers material objects and restores
    // them after every shadow frame. Keep one stable display material per mesh
    // so a KHR variant switch cannot later be overwritten by that snapshot.
    scene.traverse(object => {
        if(
            getMaterialVariantTable(object)
            && object instanceof Mesh
        ) {
            const mesh = object as MaterialMesh
            if(Array.isArray(mesh.material)) return

            const displayMaterial = resources.createMaterialCopy(
                mesh.material,
            )
            mesh.material = displayMaterial
            displayMaterials.set(object, displayMaterial)
        }
    })

    const ensureLoaded = async ():Promise<void> => {
        if(loading !== null) {
            await loading
            return
        }

        loading = ensureSourceVariants(scene, true)
            .then(() => {
                if(!resources.isDisposed()) {
                    normalizeMaterialVariantMaterials(scene, resources)
                }
            })
            .catch(error => {
                loading = null
                throw error
            })
        await loading
    }

    return {
        ensureLoaded,
        select: async (variantName, onUpdate) => {
            const revision = ++selectionRevision
            await ensureLoaded()
            if(
                resources.isDisposed()
                || revision !== selectionRevision
            ) {
                return
            }

            await selectSourceVariant(
                scene,
                variantName,
                true,
                object => {
                    const selectedMaterial = object.material
                    const displayMaterial = displayMaterials.get(object)
                    if(
                        displayMaterial
                        && displayMaterial !== selectedMaterial
                    ) {
                        copyVariantMaterial(displayMaterial, selectedMaterial)
                        object.material = displayMaterial
                    }

                    // Tables and display materials are viewer-owned before the
                    // product-specific callback is allowed to mutate them.
                    if(
                        onUpdate
                        && !resources.isDisposed()
                        && object.material instanceof MeshStandardMaterial
                    ) {
                        onUpdate(object as MaterialVariantObject & {
                            material: MeshStandardMaterial
                        })
                    }
                    // copy() and product processing can add/remove maps or
                    // change shader flags without changing material identity.
                    object.material.needsUpdate = true
                },
            )
            if(
                !resources.isDisposed()
                && revision === selectionRevision
            ) {
                normalizeMaterialVariantMaterials(scene, resources)
            }
        },
    }
}
